/**
 * Single source of truth for the site's search metadata.
 *
 * Three consumers read this file, so it stays framework-neutral CommonJS:
 *   1. <Seo> in the React app, for client-side navigation.
 *   2. scripts/generate-seo.js, which emits public/sitemap.xml at build time.
 *   3. services/api/seo/inject.js, via the seo-metadata.json the same script
 *      writes into the build — that copy is what crawlers actually see, since
 *      the SPA ships an empty <div id="root"> and nothing else.
 *
 * Adding a public page means adding it here and nowhere else.
 */

const SITE_URL = "https://photentra.com";
const SITE_NAME = "Photentra";
const DEFAULT_OG_IMAGE = `${SITE_URL}/photentra-logo.png`;

const DEFAULT_TITLE =
  "Photentra — AI Photo Delivery & Face Recognition for Photographers";
const DEFAULT_DESCRIPTION =
  "Photentra is an AI photography platform for studios. Upload event photos, auto-detect faces, share client galleries, and let clients pick their favourites — from shoot to delivery in one place.";

/**
 * Indexable pages. `path` is exact — these are the only URLs that belong in
 * the sitemap, and the only ones a crawler should treat as canonical.
 */
const PUBLIC_ROUTES = [
  {
    path: "/",
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    priority: "1.0",
    changefreq: "weekly",
    structuredData: "homepage",
  },
  {
    path: "/pricing",
    title: "Pricing — Photentra Plans for Photographers & Studios",
    description:
      "Simple, transparent pricing for AI photo delivery. Compare Starter, Pro, and Pro Max plans — client galleries, face search, contracts, and album proofing. Cancel anytime.",
    priority: "0.9",
    changefreq: "weekly",
  },
  {
    path: "/signup",
    title: "Start Free — Photentra for Photographers & Studios",
    description:
      "Create a free Photentra account. Unlimited client galleries, AI face search, and album proofing on the Starter plan. No credit card required.",
    priority: "0.8",
    changefreq: "monthly",
  },
  {
    path: "/privacy",
    title: "Privacy Policy | Photentra",
    description:
      "How Photentra collects, stores, and protects photographer and client data, including face embeddings and uploaded photographs.",
    priority: "0.3",
    changefreq: "yearly",
  },
  {
    path: "/terms",
    title: "Terms of Service | Photentra",
    description:
      "The terms governing use of Photentra's photo management, client gallery, and facial recognition services.",
    priority: "0.3",
    changefreq: "yearly",
  },
];

/**
 * Pages that must never be indexed, matched as path prefixes.
 *
 * The token routes are the sensitive half: a client gallery, QR share, payment
 * link, or frame order is private only because its URL is unguessable. One
 * leaked link in a public forum is enough for a crawler to find it, so these
 * carry an explicit noindex rather than a robots.txt Disallow — a disallowed
 * page can't be crawled, which means the noindex is never read, which is the
 * exact failure we're guarding against.
 *
 * The app routes are the other half: behind auth, so a crawler only ever sees
 * a login redirect. Those are duplicate content and wasted crawl budget, and
 * are additionally blocked in robots.txt where a stray indexed URL is harmless.
 */
const PRIVATE_PREFIXES = [
  "/shared/",
  "/qr/",
  "/pay/",
  "/frame-order/",
  "/v/",
  "/event/",
  "/auth/callback",
  "/login",
  "/forgot-password",
  "/reset-password/",
  "/shan-anusha-babe",
  "/dashboard",
  "/events",
  "/live-sync",
  "/wallet",
  "/subscribe/",
  "/facial-recognition",
  "/profile",
  "/qr-sharing",
  "/ai",
  "/reminders",
  "/team",
  "/ad-banners",
  "/my-website",
  "/photo-frames",
  "/photo-frame-orders",
  "/admin",
];

/**
 * JSON-LD attached to the homepage only.
 *
 * Organization and WebSite describe the brand and are what a knowledge panel
 * and sitelinks search box are built from. SoftwareApplication describes the
 * product itself.
 *
 * The offer is pinned to the Starter plan's genuine $0 rather than a paid tier:
 * every other price is per-currency and read from the database at runtime, so
 * hardcoding one here would start drifting from the pricing table the moment
 * anyone edits a plan — and a schema price that contradicts the visible page is
 * worse than no price at all.
 *
 * Deliberately absent: FAQPage. The FAQ copy is overridable from the admin CMS
 * (landing_content), so a static schema block would describe questions the page
 * may no longer show. Google also narrowed FAQ rich results to health and
 * government sites in 2023, so the upside no longer covers that risk.
 */
const HOMEPAGE_STRUCTURED_DATA = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    description:
      "AI-powered photo management, facial recognition, client galleries and album proofing for professional photographers and studios.",
    sameAs: ["https://twitter.com/photentra", "https://instagram.com/photentra"],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE_NAME,
    applicationCategory: "PhotographyApplication",
    operatingSystem: "Web, Windows, macOS, iOS, Android",
    url: SITE_URL,
    description:
      "Photentra gives photography studios AI facial recognition, shareable client galleries, client photo selection, album proofing and delivery in a single workflow.",
    featureList: [
      "AI facial recognition and face search across an event",
      "Shareable client galleries with no client account required",
      "Client photo selection and rejection",
      "Album proofing with on-page markup",
      "QR code gallery sharing for event guests",
      "Studio websites on a custom domain",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description: "Free forever on the Starter plan. No credit card required.",
    },
  },
];

/** Prefixes crawlers may index but that are too numerous for the sitemap. */
const CRAWLABLE_PREFIXES = ["/portfolio/"];

const isPrivatePath = (pathname) =>
  PRIVATE_PREFIXES.some(
    (prefix) =>
      pathname === prefix ||
      pathname === prefix.replace(/\/$/, "") ||
      pathname.startsWith(prefix)
  );

const findPublicRoute = (pathname) => {
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : pathname;
  return PUBLIC_ROUTES.find((route) => route.path === normalized) || null;
};

module.exports = {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  PUBLIC_ROUTES,
  HOMEPAGE_STRUCTURED_DATA,
  PRIVATE_PREFIXES,
  CRAWLABLE_PREFIXES,
  isPrivatePath,
  findPublicRoute,
};
